/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support..newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

import { ConditionConfigurationContext, DefaultConditionConfigurationContext, DefaultFormErrorBoundaryConfigurationContext, FormContext, FormErrorActionContext, FormErrorBoundary, FormErrorBoundaryConfigurationContext, FormErrorBoundaryMode, FormManager, IConditionConfigurationContext, IFormErrorBoundaryConfigurationContext, IUseFormOptions, useForm } from "@ngt/forms-core";
import { FormEvent, MutableRefObject, PropsWithChildren, ReactNode, useCallback, useContext, useEffect, useMemo } from "react";

/*
 * ---------------------------------------------------------------------------------
 * This file contains the component that provides context for the online patient
 * management system.
 * ---------------------------------------------------------------------------------
 */

/*
 * ----------------------------------------------------------------------------------
 * Imports - External
 * ----------------------------------------------------------------------------------
 */

/*
 * Required to use React components.
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */

/**
 * This interface defines the properties for the Form component.
 */
export interface IFormProps<TValues extends object = any, TError = any> extends IUseFormOptions<TValues, TError>, PropsWithChildren {
    ConditionConfiguration?: Partial<IConditionConfigurationContext<TValues, TError>>;
    FormErrorBoundaryConfiguration?: Partial<IFormErrorBoundaryConfigurationContext<TValues, TError>>;
    formRef?: MutableRefObject<FormManager<TValues, TError> | null>;
    globalErrorPropertyPath?: string;
}

const emptyFieldContext = { name: undefined, actions: undefined } as any;

/*
 * ---------------------------------------------------------------------------------
 * Components
 * ---------------------------------------------------------------------------------
 */
const MySuperAwesomeForm = <TValues extends object = any, TError = any>({
    ...props
}: IFormProps<TValues, TError>) => {
    return (
        <FormErrorBoundary mode={FormErrorBoundaryMode.Contain}>
            <InternalMySuperAwesomeForm {...props} />
        </FormErrorBoundary>
    );
};
/**
 * This component provides context for the patient management system.
 * @param param0 component properties.
 */
const InternalMySuperAwesomeForm = <TValues extends object = any, TError = any>({
    children,
    ConditionConfiguration,
    FormErrorBoundaryConfiguration,
    formRef,
    globalErrorPropertyPath,
    ...useFormOptions
}: IFormProps<TValues, TError>) => {
    const formMethods = useForm<TValues, TError>(useFormOptions, formRef);

    const conditionConfigurationContext = useMemo(() => {
        return {
            onConditionNotMet: ConditionConfiguration?.onConditionNotMet ?? DefaultConditionConfigurationContext.onConditionNotMet,
            view: ConditionConfiguration?.view ?? DefaultConditionConfigurationContext.view
        }
    }, [ConditionConfiguration?.view, ConditionConfiguration?.onConditionNotMet]);

    const formErrorBoundaryConfigurationContext = useMemo(() => {
        return {
            includeError: FormErrorBoundaryConfiguration?.includeError ?? DefaultFormErrorBoundaryConfigurationContext.includeError,
            subscription: FormErrorBoundaryConfiguration?.subscription ?? DefaultFormErrorBoundaryConfigurationContext.subscription
        }
    }, [FormErrorBoundaryConfiguration?.subscription, FormErrorBoundaryConfiguration?.includeError]);

    const formErrorActionContext = useContext(FormErrorActionContext);

    useEffect(() => {
        if (formErrorActionContext?.addPropertyPath && formErrorActionContext?.removePropertyPath && globalErrorPropertyPath) {
            formErrorActionContext?.addPropertyPath(globalErrorPropertyPath)

            return () => {
                formErrorActionContext?.removePropertyPath(globalErrorPropertyPath);
            }
        }

        return () => { };
    }, [formErrorActionContext?.addPropertyPath, formErrorActionContext?.removePropertyPath, globalErrorPropertyPath])

    return (
        <ConditionConfigurationContext.Provider value={conditionConfigurationContext}>
            <FormErrorBoundaryConfigurationContext.Provider value={formErrorBoundaryConfigurationContext}>
                <FormContext.Provider value={formMethods}>
                    {children}
                </FormContext.Provider>
            </FormErrorBoundaryConfigurationContext.Provider>
        </ConditionConfigurationContext.Provider>
    );
}

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */

export default MySuperAwesomeForm;